import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { api } from "@/lib/api";
import { apiError } from "@/lib/platform";
import { useAuth } from "@/context/AuthContext";

export const ApprovalRequestDialog = ({ open, onOpenChange, target, onRequested }) => {
  const auth = useAuth();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [users, setUsers] = useState([]);
  const [approvers, setApprovers] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTitle(target?.title ? `Approve: ${target.title}` : "");
    setDescription(""); setDueDate(""); setApprovers([]);
    api.get("/platform/users/directory").then(({ data }) =>
      setUsers(data.users.filter((u) => u.username !== auth.user?.username))).catch(() => {});
  }, [open, target, auth.user?.username]);

  const toggle = (u) => setApprovers((xs) => (xs.includes(u) ? xs.filter((x) => x !== u) : [...xs, u]));

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/platform/approvals", {
        module: target.module, object_type: target.object_type, object_id: target.object_id,
        title, description, approvers, due_date: dueDate || null,
      });
      toast.success(`Approval ${data.approval_ref} requested`);
      onRequested?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(apiError(e, "Could not request approval"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="approval-request-dialog">
        <DialogHeader>
          <DialogTitle>Request approval</DialogTitle>
          <DialogDescription>Leave approvers empty to route to anyone whose role can approve this module.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="approval-request-title" />
          </div>
          <div className="space-y-1.5">
            <Label>Context for approvers</Label>
            <Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)}
                      data-testid="approval-request-description" />
          </div>
          <div className="space-y-1.5">
            <Label>Decision needed by</Label>
            <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} data-testid="approval-request-due" />
          </div>
          <div className="space-y-1.5">
            <Label>Specific approvers (optional)</Label>
            <div className="max-h-40 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
              {users.map((u) => (
                <label key={u.username} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50">
                  <Checkbox checked={approvers.includes(u.username)} onCheckedChange={() => toggle(u.username)}
                            data-testid={`approval-approver-${u.username}`} />
                  <span className="text-[#003366]">{u.full_name || u.username}</span>
                  <span className="text-[10px] font-mono text-slate-400 ml-auto">{u.grc_role}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving || !title.trim()} className="bg-[#003366] hover:bg-[#003366]/90"
                  data-testid="approval-request-submit">
            {saving ? "Requesting…" : "Request approval"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
